import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import JSZip from "jszip";
import { saveAs } from "file-saver";

import { State } from "../types";
import {
  fetchReceipt,
  formatAmount,
  formatDate,
  getLocalDate,
  getUserName,
  queryList,
  stringWithoutManualLineBreaks,
} from "../utils";
import { Storage } from "aws-amplify";
import { utils, write } from "xlsx";

import IconDownload from "../assets/icons/download.svg";
import { BookkeepingFull, Tax, TransactionFull } from "../models";
import {
  AccountingSystem,
  AccountingSystemConfig,
  AccountingSystemConfigsByBookkeepingIdQuery,
  AccountingSystemConfigsByBookkeepingIdQueryVariables,
  CurrencyCode,
  AccountingSystemReimbursementsByAccountingSystemBillIdQuery,
  AccountingSystemReimbursementsByAccountingSystemBillIdQueryVariables,
  ListAccountingSystemBillsQuery,
  ListAccountingSystemBillsQueryVariables,
  AccountingSystemBill as AccountingSystemBillType,
  TaxType,
  TransactionStatus,
  UserBookkeeping,
  UserBookkeepingsByBookkeepingIdQuery,
  UserBookkeepingsByBookkeepingIdQueryVariables,
} from "../API";
import { useEffect, useRef, useState } from "react";
import Tooltip from "./tooltip";
import { markTransactionsExported } from "../utils/transactions/markTransactionsExported";
import { saveWithExportMark } from "../utils/transactions/saveWithExportMark";
import {
  accountingSystemConfigsByBookkeepingId,
  userBookkeepingsByBookkeepingId,
  listAccountingSystemBills,
  accountingSystemReimbursementsByAccountingSystemBillId,
} from "../graphql/queries";
import { format } from "date-fns";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { generateTopalXml, TopalValidationError } from "../utils/topal/topal-export-orchestrator";
import { ReimbursementInput } from "../utils/topal/topal-employee-mapper";

Storage.configure({ level: "public" });

type DownloadButtonProps = {
  bookkeeping?: BookkeepingFull;
  transactions?: TransactionFull[];
  baseCurrency: CurrencyCode;
  state: State;
  dateFrom?: Date;
  dateTo?: Date;
  hasTaxes?: boolean;
  organizationName?: string;
  onExportComplete?: () => void;
};

type AggregatedTransaction = {
  id: string;
  bookingAccountId: string;
  amount: number;
  bookingAccount: {
    accountNumber: string;
    taxType: TaxType;
  };
  paidAt: string;
  title: string;
  taxes: Tax[];
};

type TransactionAccumulator = {
  [key: string]: AggregatedTransaction;
};

const DownloadButton = ({
  bookkeeping,
  transactions: transactionsProp,
  baseCurrency,
  state,
  dateFrom,
  dateTo,
  hasTaxes,
  organizationName,
  onExportComplete,
}: DownloadButtonProps) => {
  // Every flow but the accounting exports sees the full list; the four
  // accounting flows shadow this with an extracted-filtered copy.
  const transactions = transactionsProp;
  const [isLoading, setIsLoading] = useState(false);
  const [accountingSystemConfig, setAccountingSystemConfig] =
    useState<AccountingSystemConfig>();
  const toast = useToast();

  // Re-export guard state (Plan 09-03)
  const {
    isOpen: isReexportOpen,
    onOpen: onReexportOpen,
    onClose: onReexportClose,
  } = useDisclosure();
  const reexportCancelRef = useRef<HTMLButtonElement>(null);
  const [pendingExport, setPendingExport] = useState<
    (() => Promise<void>) | null
  >(null);
  const [reexportCounts, setReexportCounts] = useState<{
    already: number;
    total: number;
  }>({ already: 0, total: 0 });
  const [isConfirming, setIsConfirming] = useState(false);

  const onReexportCloseClearing = () => {
    setPendingExport(null);
    onReexportClose();
  };

  const guardedRun = (fn: () => Promise<void>) => async () => {
    if (!transactions) return;
    const already = transactions.filter((t) => t.exportedAt).length;
    if (already > 0) {
      // Snapshot the click-time counts AND store fn for confirm. The
      // closure naturally freezes `transactions` since each render
      // produces a fresh fn that closes over the then-current value, so
      // the dialog body and the eventual export operate on the same
      // snapshot — even if a refetch swaps `transactions` while the
      // dialog is open.
      setReexportCounts({ already, total: transactions.length });
      // IMPORTANT: functional setter form is required because fn is itself
      // a function. `setPendingExport(fn)` would invoke fn(prev) and store
      // the result instead of storing fn.
      setPendingExport(() => fn);
      onReexportOpen();
    } else {
      await fn();
    }
  };

  const markAndToast = async () => {
    if (!transactions) return;
    // Extracted rows are never part of an accounting export and marking them
    // exported would hide them from the inbox (exportedAt filter).
    const ids = transactions
      .filter((t) => t.status !== TransactionStatus.extracted)
      .map((t) => t.id);
    const { success, failed } = await markTransactionsExported(ids);
    if (failed.length > 0) {
      toast({
        title:
          failed.length === ids.length
            ? "Markierung fehlgeschlagen"
            : "Export teilweise gespeichert",
        description:
          failed.length === ids.length
            ? "Datei wurde heruntergeladen, aber keine Transaktion konnte als exportiert markiert werden. Bitte später erneut exportieren."
            : `${success.length} von ${ids.length} Transaktionen markiert. ${failed.length} nicht gespeichert.`,
        status: failed.length === ids.length ? "error" : "warning",
        duration: failed.length === ids.length ? 8000 : 6000,
        isClosable: true,
      });
    }
    // Only refresh the table when at least one transaction was actually
    // marked. If every mark failed, nothing changed server-side and a
    // refetch would clear the user's selection over a no-op.
    if (success.length > 0) onExportComplete?.();
  };

  useEffect(() => {
    if (bookkeeping?.id) {
      queryList<
        AccountingSystemConfig,
        AccountingSystemConfigsByBookkeepingIdQueryVariables,
        AccountingSystemConfigsByBookkeepingIdQuery
      >(accountingSystemConfigsByBookkeepingId, {
        bookkeepingId: bookkeeping.id,
      })
        .then((response) => {
          if (response.length > 0) setAccountingSystemConfig(response[0]);
        })
        .catch((error) =>
          console.error("Error fetching AccountingSystemConfig", error)
        );
    }
  }, [bookkeeping?.id]);

  const isTopal = bookkeeping?.accountingSystem === AccountingSystem.topal;
  const isBanana = bookkeeping?.accountingSystem === AccountingSystem.banana;
  const isAbacus = bookkeeping?.accountingSystem === AccountingSystem.abacus;
  const useMenu = isTopal || isBanana || isAbacus;
  const showReimbursementExport =
    isTopal && accountingSystemConfig?.reimbursement === true;

  const getFilename = (ext: string) => {
    const parts: string[] = [];
    if (organizationName) parts.push(organizationName);
    if (dateFrom && dateTo) {
      parts.push(
        `${format(dateFrom, "yyyy-MM-dd")}_${format(dateTo, "yyyy-MM-dd")}`
      );
    }
    return parts.length > 0
      ? `${parts.join("_")}.${ext}`
      : `Transaktionen.${ext}`;
  };

  const downloadExcel = async () => {
    if (transactions) {
      setIsLoading(true);

      var zip = new JSZip();
      const data: (string | number | object)[][] = [
        [
          "Datum",
          "Transaktion",
          "Original Betrag",
          `${baseCurrency.toUpperCase()} Betrag`,
          "MwSt. Betrag",
          "MwSt. Satz",
          "MwSt. Betrag Total",
          "Buchungskategorie",
          "Beleg",
          "Status",
          "Benutzer",
        ],
      ];

      const receiptResults = await Promise.all(
        transactions.map(async (transaction, index) => {
          const key =
            transaction.attachments.length > 0
              ? transaction.attachments[0].key
              : undefined;

          let filepath: string | undefined;
          const date = formatDate(getLocalDate(new Date(transaction.paidAt)));

          const counter = index + 1;

          if (key) {
            const receiptData = await fetchReceipt(key);

            if (receiptData && receiptData.blob) {
              filepath = `Belege/${counter}_${date}_${receiptData.key}`;
              zip = zip.file(filepath, receiptData.blob, { base64: true });
            }
          }

          return {
            index,
            row: [
              date,
              transaction.title,
              formatAmount(transaction.amount, transaction.currencyCode),
              transaction.baseCurrencyAmount
                ? formatAmount(transaction.baseCurrencyAmount, baseCurrency)
                : formatAmount(transaction.amount, baseCurrency),
              transaction.taxes
                .map((tax) =>
                  tax.amount
                    ? formatAmount(
                        transaction.taxes.length > 1
                          ? tax.amount
                          : Math.round(
                              ((transaction.amount * tax.taxRate.rate) /
                                (100 + tax.taxRate.rate)) *
                                100
                            ) / 100,
                        transaction.currencyCode
                      )
                    : ""
                )
                .filter((amount) => amount)
                .join(" | "),
              transaction.taxes
                .map((tax) => `${tax.taxRate.rate}%`)
                .filter((rate) => rate)
                .join(" | "),
              transaction.taxes
                .map((tax) =>
                  tax.amount
                    ? formatAmount(
                        transaction.taxes.length > 1
                          ? tax.totalAmount ?? transaction.amount
                          : transaction.amount,
                        transaction.currencyCode
                      )
                    : ""
                )
                .filter((amount) => amount)
                .join(" | "),
              stringWithoutManualLineBreaks(transaction.bookingAccount.name),
              filepath ?? "",
              transaction.status === TransactionStatus.booked
                ? "Abgeschlossen"
                : transaction.status === TransactionStatus.draft
                ? "Entwurf"
                : "Unvollständig",
              getUserName(transaction.user),
            ],
          };
        })
      );

      // Sort the results based on the original order and push into the data array
      receiptResults
        .sort((a, b) => a.index - b.index)
        .forEach((result) => {
          data.push(result.row);
        });

      // Create worksheet and zip file
      const worksheet = utils.aoa_to_sheet(data);

      // Create hyperlinks
      for (let i = 1; i < data.length; i++) {
        const cellRef = `I${i + 1}`;
        const cellValue = worksheet[cellRef].v;
        worksheet[cellRef].l = { Target: cellValue };
      }

      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, "Transaktionen");

      zip.file(
        getFilename("xlsx"),
        write(workbook, { bookType: "xlsx", type: "binary" }),
        { binary: true }
      );

      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, getFilename("zip"));

      setIsLoading(false);
    }
  };

  // Example Export CSV
  //
  // BlgNr  Belegnummer
  // Date	  Buchungsdatum
  // AccId	Fibu-Konto
  // Grp	  Beleg-Gruppe (2-stelliger alphanumerischer Code)
  // CAcc	  Gegenkonto (optional)
  // Type	  Soll/Haben (Soll=0, Haben=1)
  // TaxId	MWST-Code
  // BType	Buchungsart (Buchung=0,Kostenstellenbuchung=1, MWST-Buchung=2)
  // ValBt	Buchungsbetrag Brutto, MWST wird anhand des MWST-Codes von Topal berechnet
  // Text	  Buchungstext
  // MkTxB	Technischer Flag für MWST-Behandlung, muss 1 sein
  //
  // BlgNr;Date;AccId;Type;TaxId;BType;ValBt;Text;MkTxB
  // 16054;03.07.2023;1000;0;;0;6526.70;"Honorar";1
  // 16054;03.07.2023;6000;1;;0;6526.70;"Honorar";1
  // 16055;03.07.2023;1000;0;;0;2352.90;"Helsana KK";1
  // 16055;03.07.2023;2487;1;;0;2352.90;"Helsana KK";1
  const downloadTopal = async ({ aggregate }: { aggregate: boolean }) => {
    // Accounting exports never carry extracted rows — unverified inbox items,
    // same rule as Bexio and the Saldo. The Excel export keeps them.
    const transactions = transactionsProp?.filter(
      (t) => t.status !== TransactionStatus.extracted
    );
    if (transactions && dateTo) {
      if (!accountingSystemConfig) return;
      setIsLoading(true);

      const accountNumberCash =
        accountingSystemConfig?.senderBankAccountId ?? 1000;

      const data: (string | number | object)[][] = [
        [
          "BlgNr", // Blg
          "Date", // Datum
          "AccId", // Kto
          "Type", // S/H
          "TaxId", // SId
          "BType",
          "ValBt", // Netto
          "Text", // Tx1
          "MkTxB",
        ],
      ];

      const aggregatedTransactions = getAggregatedTransactions(
        transactions.map((t) => toBaseCurrencyTransaction(t, baseCurrency)),
        aggregate
      );

      for (const [
        index,
        aggregatedTransaction,
      ] of aggregatedTransactions.entries()) {
        const date = formatDate(
          aggregate
            ? dateTo
            : getLocalDate(new Date(aggregatedTransaction.paidAt))
        );

        const taxFirst = aggregatedTransaction.taxes[0];

        // Credit account first
        data.push([
          index + 1,
          date,
          accountNumberCash,
          aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
            ? 0
            : 1,
          aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
            ? taxFirst?.taxRate.code?.replace("NO_EXPORT", "") ?? ""
            : "",
          0,
          aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
            ? aggregatedTransaction.taxes.length > 1
              ? taxFirst?.totalAmount ?? aggregatedTransaction.amount
              : aggregatedTransaction.amount
            : aggregatedTransaction.amount,
          stringWithoutManualLineBreaks(aggregatedTransaction.title),
          1,
        ]);

        // Debit account for first tax
        data.push([
          index + 1,
          date,
          aggregatedTransaction.bookingAccount.accountNumber,
          aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
            ? 1
            : 0,
          aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
            ? ""
            : taxFirst?.taxRate.code?.replace("NO_EXPORT", "") ?? "",
          0,
          aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
            ? aggregatedTransaction.amount
            : aggregatedTransaction.taxes.length > 1
            ? taxFirst?.totalAmount ?? aggregatedTransaction.amount
            : aggregatedTransaction.amount,
          stringWithoutManualLineBreaks(aggregatedTransaction.title),
          1,
        ]);

        // Additional taxes (if any)
        for (let i = 1; i < aggregatedTransaction.taxes.length; i++) {
          const tax = aggregatedTransaction.taxes[i];
          data.push([
            index + 1,
            date,
            aggregatedTransaction.bookingAccount.accountNumber,
            aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
              ? 1
              : 0,
            aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
              ? ""
              : tax.taxRate.code?.replace("NO_EXPORT", "") ?? "",
            0,
            aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
              ? aggregatedTransaction.amount
              : tax.totalAmount ?? aggregatedTransaction.amount,
            stringWithoutManualLineBreaks(aggregatedTransaction.title),
            1,
          ]);
        }
      }

      const worksheet = utils.aoa_to_sheet(data);

      const workbook = utils.book_new();

      utils.book_append_sheet(workbook, worksheet, "Transaktionen");

      const wbout = write(workbook, { bookType: "csv", type: "binary" });

      // Convert binary string to a buffer to remove BOM
      const buffer = new ArrayBuffer(wbout.length);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < wbout.length; i++)
        view[i] = wbout.charCodeAt(i) & 0xff;

      // Create blob from the buffer
      const blob = new Blob([buffer], { type: "text/csv;charset=utf-8" });

      await saveWithExportMark({
        blob,
        filename: getFilename("csv"),
        mimeType: "text/csv",
        extension: "csv",
        onSaved: markAndToast,
      });

      setIsLoading(false);
    }
  };

  // Example Export TXT
  // Date;Doc;DocInvoice;Description;AccountDebit;AccountCredit;Amount;VatCode;VatAmountType;VatRate;VatPosted;ExternalReference
  // 05.01.2024;1;;Rechnung 103 an Firma 1;110001;3000;5000.00;V81;;-8.10;-374.65;expensly-2024-01-05-d51e2315
  // 05.01.2024;2;;Bareinzahlung Kasse;1000;1020;2000.00;;;;expensly-2024-01-05-56139481
  // 30.03.2024;3;;Zahlungseingang Rechnung 103;1020;110001;5000.00;;;;expensly-2024-03-30-42b66dd4
  const downloadBanana = async ({ aggregate }: { aggregate: boolean }) => {
    const transactions = transactionsProp?.filter(
      (t) => t.status !== TransactionStatus.extracted
    );
    if (transactions && dateTo) {
      setIsLoading(true);

      const accountNumberCash =
        accountingSystemConfig?.senderBankAccountId ?? 1000;

      const data: (string | number | object)[][] = [
        [
          "Date",
          ...(aggregate ? [] : ["DocInvoice"]),
          "Description",
          "AccountDebit",
          "AccountCredit",
          "Amount",
          "VatCode",
          ...(aggregate ? [] : ["ExternalReference"]),
        ],
      ];

      const aggregatedTransactions = getAggregatedTransactions(
        transactions.map((t) => toBaseCurrencyTransaction(t, baseCurrency)),
        aggregate
      );

      for (const aggregatedTransaction of aggregatedTransactions) {
        const date = format(
          aggregate
            ? dateTo
            : getLocalDate(new Date(aggregatedTransaction.paidAt)),
          "yyyy-MM-dd"
        );

        const reference = aggregate
          ? ""
          : `expensly-${aggregatedTransaction.id.split("-")[0]}`;

        const taxFirst = aggregatedTransaction.taxes[0];

        if (aggregatedTransaction.taxes.length > 1) {
          // Composed booking (multiple taxes)
          data.push([
            date,
            reference,
            stringWithoutManualLineBreaks(aggregatedTransaction.title),
            aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
              ? accountNumberCash
              : "",
            aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
              ? ""
              : accountNumberCash,
            aggregatedTransaction.amount,
            "",
            reference,
          ]);

          // Add entry for each tax
          for (const tax of aggregatedTransaction.taxes) {
            data.push([
              date,
              reference,
              stringWithoutManualLineBreaks(aggregatedTransaction.title),
              aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
                ? ""
                : aggregatedTransaction.bookingAccount.accountNumber,
              aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
                ? aggregatedTransaction.bookingAccount.accountNumber
                : "",
              tax.totalAmount ?? aggregatedTransaction.amount,
              tax.taxRate.code?.replace("NO_EXPORT", "") ?? "",
              reference,
            ]);
          }
        } else {
          // Single booking
          data.push([
            date,
            reference,
            stringWithoutManualLineBreaks(aggregatedTransaction.title),
            aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
              ? accountNumberCash
              : aggregatedTransaction.bookingAccount.accountNumber,
            aggregatedTransaction.bookingAccount.taxType === TaxType.earnings
              ? aggregatedTransaction.bookingAccount.accountNumber
              : accountNumberCash,
            aggregatedTransaction.amount,
            taxFirst?.taxRate.code?.replace("NO_EXPORT", "") ?? "",
            reference,
          ]);
        }
      }

      const worksheet = utils.aoa_to_sheet(data);

      const workbook = utils.book_new();

      utils.book_append_sheet(workbook, worksheet, "Transaktionen");

      const wbout = write(workbook, { bookType: "csv", type: "binary" });

      // Convert binary string to a buffer to remove BOM
      const buffer = new ArrayBuffer(wbout.length);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < wbout.length; i++)
        view[i] = wbout.charCodeAt(i) & 0xff;

      // Create blob from the buffer
      const blob = new Blob([buffer], { type: "text/csv;charset=utf-8" });

      await saveWithExportMark({
        blob,
        filename: getFilename("txt"),
        mimeType: "text/plain",
        extension: "txt",
        onSaved: markAndToast,
      });

      setIsLoading(false);
    }
  };

  // Abacus Export XLSX
  // Essential columns: Datum, Konto, Gegenkonto, Text1, Betrag, Text2, Buchungsebene 1, Gegenseite Buchungsebene 1, Mwst Code, Mwst Satz, Mwst Incl
  const downloadAbacus = async ({ aggregate }: { aggregate: boolean }) => {
    const transactions = transactionsProp?.filter(
      (t) => t.status !== TransactionStatus.extracted
    );
    if (transactions && dateTo) {
      setIsLoading(true);

      const accountNumberCash =
        accountingSystemConfig?.senderBankAccountId ?? 1000;

      const data: (string | number | object)[][] = [
        [
          "Datum",
          "Konto",
          "Gegenkonto",
          "Text1",
          "Betrag",
          "Text2",
          "Buchungsebene 1",
          "Gegenseite Buchungsebene 1",
          "Mwst Code",
          "Mwst Satz",
          "Mwst Incl",
        ],
      ];

      const aggregatedTransactions = getAggregatedTransactions(
        transactions.map((t) => toBaseCurrencyTransaction(t, baseCurrency)),
        aggregate
      );

      for (const aggregatedTransaction of aggregatedTransactions) {
        const date = format(
          aggregate
            ? dateTo
            : getLocalDate(new Date(aggregatedTransaction.paidAt)),
          "dd.MM.yyyy"
        );

        const taxFirst = aggregatedTransaction.taxes[0];
        const isEarnings =
          aggregatedTransaction.bookingAccount.taxType === TaxType.earnings;

        // For expenses: Konto = booking account, Gegenkonto = cash
        // For earnings: Konto = cash, Gegenkonto = booking account
        const konto = isEarnings
          ? accountNumberCash
          : aggregatedTransaction.bookingAccount.accountNumber;
        const gegenkonto = isEarnings
          ? aggregatedTransaction.bookingAccount.accountNumber
          : accountNumberCash;

        // Handle single or multiple taxes
        if (aggregatedTransaction.taxes.length <= 1) {
          // Single tax entry — ignore tax.totalAmount (matches Bexio sync
          // semantics: only relevant when split across >1 taxes).
          data.push([
            date,
            konto,
            gegenkonto,
            stringWithoutManualLineBreaks(aggregatedTransaction.title),
            aggregatedTransaction.amount,
            "", // Text2 - empty
            0, // Buchungsebene 1
            0, // Gegenseite Buchungsebene 1
            taxFirst?.taxRate.code?.replace("NO_EXPORT", "") ?? "",
            taxFirst?.taxRate.rate ?? 0,
            isEarnings ? "E" : "I", // E = Exclusive for earnings, I = Inclusive for expenses
          ]);
        } else {
          // Multiple taxes - create separate entries for each tax
          for (const tax of aggregatedTransaction.taxes) {
            data.push([
              date,
              konto,
              gegenkonto,
              stringWithoutManualLineBreaks(aggregatedTransaction.title),
              tax.totalAmount ?? aggregatedTransaction.amount,
              "", // Text2 - empty
              0, // Buchungsebene 1
              0, // Gegenseite Buchungsebene 1
              tax.taxRate.code?.replace("NO_EXPORT", "") ?? "",
              tax.taxRate.rate ?? 0,
              isEarnings ? "E" : "I",
            ]);
          }
        }
      }

      const worksheet = utils.aoa_to_sheet(data);
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, "Transaktionen");

      const wbout = write(workbook, { bookType: "xlsx", type: "binary" });

      // Convert binary string to buffer
      const buffer = new ArrayBuffer(wbout.length);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < wbout.length; i++)
        view[i] = wbout.charCodeAt(i) & 0xff;

      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      await saveWithExportMark({
        blob,
        filename: getFilename("xlsx"),
        mimeType:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        extension: "xlsx",
        onSaved: markAndToast,
      });

      setIsLoading(false);
    }
  };

  const downloadTopalXml = async () => {
    const transactions = transactionsProp?.filter(
      (t) => t.status !== TransactionStatus.extracted
    );
    if (!transactions || !dateTo || !accountingSystemConfig || !bookkeeping) return;

    setIsLoading(true);
    try {
      // Fetch UserBookkeepings for this bookkeeping
      const userBookkeepings = await queryList<
        UserBookkeeping,
        UserBookkeepingsByBookkeepingIdQueryVariables,
        UserBookkeepingsByBookkeepingIdQuery
      >(userBookkeepingsByBookkeepingId, {
        bookkeepingId: bookkeeping.id,
      });

      // Get unique employee IDs from selected transactions
      const uniqueUserIds = [
        ...new Set(transactions.map((transaction) => transaction.user.id)),
      ];

      // Fetch bills by userBookkeepingId filter (same pattern as team.tsx)
      const relevantUserBookkeepings = userBookkeepings.filter((ub) =>
        uniqueUserIds.includes(ub.userId)
      );

      let billSummaries: AccountingSystemBillType[] = [];
      if (relevantUserBookkeepings.length > 0) {
        billSummaries = await queryList<
          AccountingSystemBillType,
          ListAccountingSystemBillsQueryVariables,
          ListAccountingSystemBillsQuery
        >(listAccountingSystemBills, {
          filter: {
            or: relevantUserBookkeepings.map((ub) => ({
              userBookkeepingId: { eq: ub.id },
            })),
          },
        });
      }

      // Fetch reimbursement data per bill via accountingSystemBillId index
      const employeeReimbursements = new Map<string, ReimbursementInput>();

      await Promise.all(
        billSummaries.map(async (billSummary) => {
          const userBookkeeping = relevantUserBookkeepings.find(
            (ub) => ub.id === billSummary.userBookkeepingId
          );
          if (!userBookkeeping) return;

          const reimbursements = await queryList<
            any,
            AccountingSystemReimbursementsByAccountingSystemBillIdQueryVariables,
            AccountingSystemReimbursementsByAccountingSystemBillIdQuery
          >(accountingSystemReimbursementsByAccountingSystemBillId, {
            accountingSystemBillId: billSummary.id,
          });

          if (reimbursements.length > 0) {
            const reimbursement = reimbursements[0];
            employeeReimbursements.set(userBookkeeping.userId, {
              receiverIban: reimbursement.receiverIban,
              receiverStreet: reimbursement.receiverStreet,
              receiverHouseNumber: reimbursement.receiverHouseNumber,
              receiverCity: reimbursement.receiverCity,
              receiverPostalCode: reimbursement.receiverPostalCode,
              receiverCountry: reimbursement.receiverCountry,
            });
          }
        })
      );

      // Validate sammelkonto
      const sammelkontoCode = accountingSystemConfig.senderBankAccountId;
      if (!sammelkontoCode) {
        toast({ title: 'Export nicht möglich', description: 'Kein Sammelkonto konfiguriert.', status: 'error', duration: 9000, isClosable: true });
        setIsLoading(false);
        return;
      }

      // Generate XML via orchestrator
      const xmlString = generateTopalXml({
        transactions,
        exportDate: dateTo,
        baseCurrency: baseCurrency,
        sammelkontoCode: String(sammelkontoCode),
        employeeReimbursements,
      });

      // Encode as ISO-8859-1
      const buffer = new ArrayBuffer(xmlString.length);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < xmlString.length; i++) {
        view[i] = xmlString.charCodeAt(i) & 0xff;
      }

      // Download
      const blob = new Blob([buffer], {
        type: "application/xml;charset=ISO-8859-1",
      });
      await saveWithExportMark({
        blob,
        filename: getFilename("xml"),
        mimeType: "application/xml",
        extension: "xml",
        onSaved: markAndToast,
      });
    } catch (error) {
      if (error instanceof TopalValidationError) {
        datadogLogs.logger.warn("Topal XML validation failed", {
          bookkeepingId: bookkeeping?.id,
          transactionCount: transactions?.length ?? 0,
          message: error.message,
        });
        toast({
          title: "Export nicht möglich",
          description: error.message,
          status: "error",
          duration: null,
          isClosable: true,
        });
      } else {
        const wrappedError =
          error instanceof Error ? error : new Error(String(error));
        datadogRum.addError(wrappedError, {
          context: "topal-xml-export",
          bookkeepingId: bookkeeping?.id,
          transactionCount: transactions?.length ?? 0,
        });
        datadogLogs.logger.error(
          "Topal XML export failed",
          {
            bookkeepingId: bookkeeping?.id,
            transactionCount: transactions?.length ?? 0,
          },
          wrappedError,
        );
        toast({
          title: "Export fehlgeschlagen",
          description:
            "Der XML-Export konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = state !== "success" || transactions?.length === 0;

  const iconButton = (
    <IconButton
      variant="outline"
      aria-label="Download receipt"
      icon={<Image boxSize="20px" src={IconDownload} />}
      isDisabled={isDisabled}
      isLoading={isLoading}
      onClick={useMenu ? undefined : downloadExcel}
      as={useMenu ? "span" : undefined}
    />
  );

  const label = "Exportieren";

  return useMenu ? (
    <>
      <Menu>
        <Tooltip label={label}>
          <MenuButton disabled={isDisabled}>{iconButton}</MenuButton>
        </Tooltip>
        <MenuList minWidth="auto">
          {/*
            D-06: Excel is intentionally NOT wrapped in `guardedRun` and never
            calls `markTransactionsExported`. Excel is a generic working-copy
            download (not destined for an accounting system), so it neither
            triggers the re-export dialog nor sets `exportedAt`. See
            .planning/phases/09-export-tracking/09-CONTEXT.md (D-06) and the
            re-export-confirm-dialog test "Excel menu item is NEVER guarded
            and never marks" which locks this behavior in.
          */}
          <MenuItem onClick={downloadExcel}>Excel</MenuItem>
          {isTopal && (
            <MenuItem
              onClick={guardedRun(() => downloadTopal({ aggregate: true }))}
            >
              Topal Sammelbuchungen
            </MenuItem>
          )}
          {isTopal && (
            <MenuItem
              onClick={guardedRun(() => downloadTopal({ aggregate: false }))}
            >
              Topal Einzelbuchungen
            </MenuItem>
          )}
          {showReimbursementExport && (
            <MenuItem onClick={guardedRun(downloadTopalXml)}>
              Topal Rückerstattungen
            </MenuItem>
          )}
          {isBanana && (
            <MenuItem
              onClick={guardedRun(() => downloadBanana({ aggregate: false }))}
            >
              Banana
            </MenuItem>
          )}
          {isAbacus && (
            <MenuItem
              onClick={guardedRun(() => downloadAbacus({ aggregate: false }))}
            >
              Abacus
            </MenuItem>
          )}
        </MenuList>
      </Menu>
      <AlertDialog
        isOpen={isReexportOpen}
        leastDestructiveRef={reexportCancelRef}
        onClose={onReexportCloseClearing}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="semibold">
              Bereits exportiert
            </AlertDialogHeader>
            <AlertDialogBody>
              {`${reexportCounts.already} von ${reexportCounts.total} Transaktionen wurden bereits exportiert. Trotzdem fortfahren?`}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={reexportCancelRef}
                onClick={onReexportCloseClearing}
              >
                Abbrechen
              </Button>
              <Button
                colorScheme="orange"
                ml={3}
                isLoading={isConfirming}
                onClick={async () => {
                  // Guard against double-click: React may batch state and run
                  // the handler twice with the same captured fn before
                  // setPendingExport(null) is committed.
                  if (isConfirming) return;
                  setIsConfirming(true);
                  const fn = pendingExport;
                  setPendingExport(null);
                  onReexportClose();
                  try {
                    if (fn) await fn();
                  } finally {
                    setIsConfirming(false);
                  }
                }}
              >
                Trotzdem exportieren
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  ) : (
    <Tooltip label={label}>{iconButton}</Tooltip>
  );
};

export default DownloadButton;

// Topal/Banana/Abacus exports expect amounts in the bookkeeping's base
// currency. Source records keep `amount` and tax `totalAmount` in the
// transaction's own currency. For multi-tax splits, scale each
// `tax.totalAmount` by the txn's captured FX ratio and absorb rounding
// into the last entry so debit/credit sums stay balanced.
const toBaseCurrencyTransaction = (
  transaction: TransactionFull,
  baseCurrency: CurrencyCode
): TransactionFull => {
  const base = transaction.baseCurrencyAmount;
  if (transaction.currencyCode === baseCurrency || base == null) {
    return transaction;
  }
  if (transaction.taxes.length < 2) {
    return { ...transaction, amount: base };
  }
  if (transaction.amount === 0) {
    return {
      ...transaction,
      amount: base,
      taxes: transaction.taxes.map((tax) => ({ ...tax, totalAmount: 0 })),
    };
  }
  const ratio = base / transaction.amount;
  const round = (value: number) => parseFloat(value.toFixed(2));
  const lastIdx = transaction.taxes.length - 1;
  const scaledOthers = transaction.taxes
    .slice(0, lastIdx)
    .map((tax) =>
      tax.totalAmount != null ? round(tax.totalAmount * ratio) : null
    );
  const sumOthers = scaledOthers.reduce<number>((sum, v) => sum + (v ?? 0), 0);
  const lastTotal = round(base - sumOthers);
  return {
    ...transaction,
    amount: base,
    taxes: transaction.taxes.map((tax, idx) => ({
      ...tax,
      totalAmount: idx === lastIdx ? lastTotal : scaledOthers[idx],
    })),
  };
};

const getAggregatedTransactions = (
  transactions: TransactionFull[],
  aggregate: boolean
): AggregatedTransaction[] => {
  if (!aggregate) return transactions;

  const aggregated = transactions.reduce(
    (acc: TransactionAccumulator, transaction) => {
      const key = transaction.bookingAccountId;
      const transactionAmount =
        transaction.baseCurrencyAmount ?? transaction.amount;

      if (!acc[key]) {
        acc[key] = {
          ...transaction,
          amount: 0,
          paidAt: transaction.paidAt,
          title: transaction.bookingAccount.name,
          taxes: [],
        };
      }

      acc[key].amount += transactionAmount;

      // Aggregate taxes by taxRate.id
      for (const tax of transaction.taxes) {
        const existingTax = acc[key].taxes.find(
          (t) => t.taxRate.id === tax.taxRate.id
        );
        if (existingTax) {
          existingTax.amount = (existingTax.amount ?? 0) + (tax.amount ?? 0);
          existingTax.totalAmount =
            (existingTax.totalAmount ?? 0) +
            (tax.totalAmount ?? transactionAmount);
        } else {
          acc[key].taxes.push({
            ...tax,
            totalAmount: tax.totalAmount ?? transactionAmount,
          });
        }
      }

      return acc;
    },
    {}
  );

  return Object.values(aggregated);
};
